import {
	TiptapCollabProvider,
	TiptapCollabProviderWebsocket
} from "@tiptap-pro/provider"
import {Editor} from "./Editor"
import * as Y from "yjs"
import "./styles/simple-editor.scss"
import {
	useEffect, useState,
	//useState
} from "react"
import axios from "axios"
import {useSelector} from "react-redux"
import {selectLoadingUser} from "../../reducers/userSlice"
import Loading from "../Loading"
import {useSearchParams} from "react-router-dom"
import {useSnackbar} from "notistack"
import {getDateHourMinuteFromDateString} from "../../utils/dates"

const doc = new Y.Doc()
export default function DocViewer() {
	const [searchParams] = useSearchParams()
	const userIsLoading = useSelector(selectLoadingUser)
	const [provider, setProvider] = useState(null)
	const [lastUpdated, setLastUpdated] = useState(null)
	const [isSyncing, setIsSyncing] = useState(false)
	const [connectionError, setConnectionError] = useState(null)
	const {enqueueSnackbar} = useSnackbar()

	useEffect(() => {
		if(userIsLoading === false) {
			if(searchParams && searchParams.get("docname")) {
				axios.get(`/api/rte/open-doc/${searchParams.get("docname")}`)
					.then(res => {
						// console.log("checked in with the RZ backend, what is res: ", res.data)
						// create a ProviderWebsocket object to avoid needing to pass appId into TiptapCollabProvider
						const configuredWebsocketProvider = new TiptapCollabProviderWebsocket({
							url: res.data.connectionUrl,
							baseUrl: res.data.connectionUrl,
						})

						const newProvider = new TiptapCollabProvider({
							name: res.data.docName,
							token: res.data.token,
							document: doc,
							preserveConnection: false, // Prevents reconnection attempts after provider is torn down
							broadcast: false, // currently not using Awareness features
							websocketProvider: configuredWebsocketProvider,
						})

						// an undocumented breaking change between TipTap Editor v2 and v3 is that the provider will not
						// "attach" the websocket connection UNLESS either a baseURL or an appId are provided. So... now we do it
						// after instantiation.
						newProvider.attach()

						// an unfortunate side-effect of this method is that the newProvider will still attempt to open a WS
						// connection to localhost. It doesn't cause any issues

						newProvider.on("synced", () => {
							// check for a meta.deleted flag.
							// If the flag is missing, the loaded YJS document didn't originate from our server and could be malicious.
							// If the flag is true, the document has been deleted and we should NOT connect the provider, or TT Cloud will recreate the doc
							if(newProvider.document.getMap("meta").get("deleted") !== false || newProvider.document.getMap("__tiptapcollab__config").get("deleting") === 1) {
								newProvider.configuration.websocketProvider.disconnect()
								//newProvider.disconnect()
								newProvider.detach()
								newProvider.destroy()
								setConnectionError(true)
							} else if(newProvider.document.getMap("__tiptapcollab__config").get("lastSaved")) {
								const lastSynced = newProvider.document.getMap("__tiptapcollab__config").get("lastSaved")
								setLastUpdated(getDateHourMinuteFromDateString(new Date(lastSynced)))
								setConnectionError(false)
								setIsSyncing(false)
							}
						})

						newProvider.on("message", () => {
							if(newProvider.unsyncedChanges > 1) {
								setIsSyncing(true)
							} else {
								setIsSyncing(false)
							}

							if(newProvider.document.getMap("__tiptapcollab__config").get("lastSaved")) {
								const lastSynced = newProvider.document.getMap("__tiptapcollab__config").get("lastSaved")
								setLastUpdated(getDateHourMinuteFromDateString(new Date(lastSynced)))
							}
						})

						newProvider.on("status", () => {
							if( newProvider.status !== "connected" ) { // status definitions can be found at /client/node_modules/@hocuspocus/provider/src/types.ts
								setConnectionError(true)
							}
						})

						// I could not tell you why these are both required, but the events for disconnect and destroy are weird
						newProvider.on("destroy", () => {
							setConnectionError(true)
						})
						newProvider.on("disconnect", () => {
							setConnectionError(true)
						})

						setProvider(newProvider)
					})
					.catch(err => {
						console.error("FAILED TO FETCH COLLAB TOKEN: ", err)
						if([400, 403, 500].includes(err.response.status)) {
							//if the response status is 400, 403, or 500, we've already written an error message for users.
							enqueueSnackbar(err.response.data, {
								variant: "error",
								autoHideDuration: 5000,
								onClose: () => {
									window.close()
								}
							})
						} else {
							// else: generic fallback message.
							enqueueSnackbar("Something went wrong while retrieving this document. Please try again.", {
								variant: "error",
								autoHideDuration: 5000,
								onClose: () => {
									window.close()
								}
							})
						}
					})
			} else {
				enqueueSnackbar("Malformed request for a document. Please try again or contact support.", {
					variant: "error",
					autoHideDuration: 5000,
					onClose: () => {
						window.close()
					}
				})
			}
		}

		return () => {
			setProvider(null)
		}
	}, [userIsLoading])

	return (
		<div id={"document-editor"}>
			{(provider) ?
				<Editor
					provider={provider}
					doc={doc}
					lastUpdated={lastUpdated}
					isSynching={isSyncing}
					connectionError={connectionError}
				/>
				:
				<Loading />
			}
		</div>
	)
}